import type { ReactNode } from 'react';
import { ILLUSTRATIONS } from '@constants/illustrations';
import { ERRORS } from '@locales/id/errors';
import { COMMON, STATES } from '@locales/id/messages';
import { Button } from './Button';
import { Icon } from './Icon';
import { Illustration } from './Illustration';

/*
 * The screen states shared by every page (the 10 UX states): a page passes its own sentence, never its own layout.
 * - nothing yet: EmptyState (a feature's illustration, title, one line, optional primary action) in the list card;
 * - no results for the search or filters: NoResults (compact, "Hapus filter");
 * - loading: Async's skeleton; slow network: SlowNotice;
 * - error / no internet: LoadFailed (compact, inside the card; Async uses it) or the page's PageFailed;
 * - 403: Forbidden / FilterOutOfScope; 404: NotFound / RecordMissing (RouteStates.tsx);
 * - session expired: SessionExpiredNotice (the sign-in screen); success: SuccessState; form validation: Field.
 * NoResults, SuccessState and PartFailed are only on lazy pages: PageStates.tsx (with them, in the page-kit chunk).
 */

/** A compact state inside a card: the small illustration, one line, an optional action (one calm row). */
export function CompactState({ src, children, action, role }: { src: string; children: ReactNode; action?: ReactNode; role?: 'status' | 'alert' }) {
  return (
    <div className="empty-inline has-art" role={role}>
      <Illustration src={src} size="sm" />
      <span className="empty-inline-text">{children}</span>
      {action && <span className="empty-inline-action">{action}</span>}
    </div>
  );
}

/** A view failed to load (no data on screen): the reason (no internet has its own scene) and "Coba lagi". */
export function LoadFailed({ error, onRetry }: { error: string | null; onRetry?: () => void }) {
  const offline = error === ERRORS.offline || (typeof navigator !== 'undefined' && navigator.onLine === false);
  return (
    <CompactState
      role="alert"
      src={offline ? ILLUSTRATIONS.stateOffline : ILLUSTRATIONS.stateError}
      action={
        onRetry && (
          <Button size="sm" icon="refresh" onClick={onRetry}>
            {COMMON.retry}
          </Button>
        )
      }
    >
      <b className="empty-inline-title">{offline ? STATES.offlineTitle : STATES.errorTitle}</b>
      {error ?? ERRORS.loadFailed}
    </CompactState>
  );
}

/** The network is slow: one quiet line while the view keeps loading (the same everywhere). */
export function SlowNotice({ show, children = STATES.slow }: { show: boolean; children?: ReactNode }) {
  if (!show) return null;
  return (
    <p className="slow-notice" role="status">
      <Icon name="signal" size={16} />
      <span>{children}</span>
    </p>
  );
}

/** The sign-in screen after a session ended (expired, signed out elsewhere). */
export function SessionExpiredNotice() {
  return (
    <div className="session-expired" role="alert">
      <Illustration src={ILLUSTRATIONS.stateSessionExpired} size="sm" />
      <span>
        <b>{STATES.sessionExpired.title}</b>
        {STATES.sessionExpired.text}
      </span>
    </div>
  );
}
